import React, { useMemo } from 'react';
import { TimeOfDay, WeatherType } from '../types';

interface GardenGroundProps {
  timeOfDay: TimeOfDay;
  weather: WeatherType;
}

export const GardenGround: React.FC<GardenGroundProps> = ({
  timeOfDay,
  weather,
}) => {
  const isNight = timeOfDay === 'night';
  const isSunset = timeOfDay === 'sunset';
  const isDawn = timeOfDay === 'dawn';
  const isRainy = weather === 'rainy';

  // Procedural natural grass blades along the meadow horizon (optimized for 60fps GPU performance)
  const microGrass = useMemo(() => {
    return Array.from({ length: 48 }).map((_, i) => {
      const x = (i / 48) * 1200 + Math.sin(i * 3.7) * 9;
      const h = 9 + (Math.sin(i * 2.3) + 1) * 7;
      const lean = Math.sin(i * 1.9) * 6;
      const width = 3 + (i % 3) * 1.2;
      const animKey = (i % 3) + 1; // 1, 2, or 3
      const dur = ((isRainy ? 2.8 : 4.2) + (i % 4) * 0.35).toFixed(2);
      const delay = (-(i * 0.15) % 2.5).toFixed(2);
      return { id: i, x, h, lean, width, animKey, dur, delay };
    });
  }, [isRainy]);

  return (
    <div className="relative w-full max-w-full overflow-hidden select-none pointer-events-none">
      {/* ========================================================================= */}
      {/* SEAMLESS 3D LAWN GROUND (Directly roots flowers, matches reference image) */}
      {/* ========================================================================= */}
      <div className="relative w-full h-18 sm:h-22 md:h-26 -mt-3">
        <svg
          viewBox="0 0 1200 110"
          className="w-full h-full overflow-visible"
          preserveAspectRatio="none"
        >
          <defs>
            {/* Main Meadow Horizon Gradient */}
            <linearGradient id="lawnGradRealistic" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop
                offset="0%"
                stopColor={
                  isNight
                    ? '#1b381b'
                    : isSunset
                    ? '#7a9632'
                    : isDawn
                    ? '#6e8f2f'
                    : isRainy
                    ? '#4d7528'
                    : '#78ab38'
                }
              />
              <stop
                offset="25%"
                stopColor={
                  isNight
                    ? '#132813'
                    : isSunset
                    ? '#556b20'
                    : isDawn
                    ? '#4e6b22'
                    : isRainy
                    ? '#38571c'
                    : '#5a8c27'
                }
              />
              <stop
                offset="100%"
                stopColor={
                  isNight
                    ? '#0b170b'
                    : isSunset
                    ? '#2e3810'
                    : isDawn
                    ? '#283610'
                    : isRainy
                    ? '#1e300d'
                    : '#2e4c13'
                }
              />
            </linearGradient>

            {/* Back Row Grass Crest Shadow */}
            <linearGradient id="lawnBackCrest" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop
                offset="0%"
                stopColor={
                  isNight
                    ? '#162e16'
                    : isSunset
                    ? '#5c7224'
                    : isDawn
                    ? '#536e25'
                    : isRainy
                    ? '#3d5c1f'
                    : '#62942b'
                }
              />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>

            {/* Sunlight Specular Rim on Grass Horizon */}
            <linearGradient id="lawnRimGlow" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.5" />
            </linearGradient>
          </defs>

          {/* Background Soft Rolling Mound */}
          <path
            d="M 0 110 L 0 6 Q 300 1 600 5 Q 900 8 1200 3 L 1200 110 Z"
            fill="url(#lawnBackCrest)"
            opacity="0.85"
          />

          {/* Main Foreground Smooth Lawn Plane */}
          <path
            d="M 0 110 L 0 3 Q 200 0 400 3 Q 600 0 800 3 Q 1000 0 1200 3 L 1200 110 Z"
            fill="url(#lawnGradRealistic)"
          />

          {/* Micro Grass Blades along the Horizon Line with gentle wind breeze (GPU CSS driven) */}
          <g>
            {microGrass.map((blade) => (
              <path
                key={blade.id}
                d={`M ${blade.x} 3 L ${blade.x + blade.lean} ${3 - blade.h} L ${blade.x + blade.width} 3 Z`}
                fill={
                  isNight
                    ? '#244d24'
                    : isSunset
                    ? '#8ea83e'
                    : isDawn
                    ? '#85a83b'
                    : isRainy
                    ? '#5d8c32'
                    : '#8bc34a'
                }
                opacity="0.9"
                className="micro-grass-blade"
                style={
                  {
                    animationName: `microGrassSway${blade.animKey}`,
                    animationDuration: `${blade.dur}s`,
                    animationTimingFunction: 'cubic-bezier(0.36, 0, 0.64, 1)',
                    animationIterationCount: 'infinite',
                    animationDelay: `${blade.delay}s`,
                    '--sway-amp': isRainy ? '6.5deg' : '4deg',
                    transformOrigin: `${blade.x}px 3px`,
                  } as React.CSSProperties
                }
              />
            ))}
          </g>

          {/* Horizon Subtle Glow Line */}
          {!isNight && (
            <path
              d="M 0 3 Q 200 0 400 3 Q 600 0 800 3 Q 1000 0 1200 3"
              stroke="url(#lawnRimGlow)"
              strokeWidth="1.2"
              strokeOpacity={isRainy ? 0.3 : 0.65}
              fill="none"
            />
          )}
        </svg>
      </div>
    </div>
  );
};
